import { type ReactNode, useState } from 'react';

import { Button, ButtonBase, Popover, Typography } from '@material-ui/core';

import { RiPulseLine } from '@remixicon/react';

import { PULSE_BLOCK_TYPE, type SaveToPulseSourceInput } from '../../types';
import {
  SaveToPulseDialog,
  type SaveToPulseSuccess,
} from '../SaveToPulseDialog';
import styles from './PulseNumberHighlight.module.css';

export type PulseNumberHighlightProps = {
  name: string;
  value: string;
  sourceInput: SaveToPulseSourceInput;
  onSuccess?: (info: SaveToPulseSuccess) => void;
  children: ReactNode;
};

export const PulseNumberHighlight = ({
  name,
  value,
  sourceInput,
  onSuccess,
  children,
}: PulseNumberHighlightProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openSaveDialog = () => {
    setAnchorEl(null);
    setDialogOpen(true);
  };

  return (
    <>
      <ButtonBase
        className={styles.number}
        onClick={event => setAnchorEl(event.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        aria-label={`${name}: ${value}. Save to pulse`}
      >
        {children}
      </ButtonBase>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ role: 'dialog', 'aria-label': name }}
      >
        <div className={styles.popover}>
          <Typography variant="body2" color="textSecondary">
            {name}
          </Typography>
          <Typography variant="h6">{value}</Typography>
          <div className={styles.popoverActions}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RiPulseLine size={16} />}
              onClick={openSaveDialog}
            >
              Add to pulse
            </Button>
          </div>
        </div>
      </Popover>
      {dialogOpen && (
        <SaveToPulseDialog
          isOpen={dialogOpen}
          onOpenChange={setDialogOpen}
          input={{
            ...sourceInput,
            blockType: PULSE_BLOCK_TYPE.BIG_NUMBER,
            suggestedName: name,
          }}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
};
